import React from 'react'
import './EKGImportHeader.component.css'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { getEKGImportSelector } from '../selectors'
import { IonIcon } from '@ionic/react'
import { checkmark } from 'ionicons/icons'
import skippedIcon from '../data/SkippedIcon.svg'
import useDeviceMetrics from '../../../utilities/useDeviceMetrics'

interface IEKGImportHeaderProps {
  currentStep: number
}

const EKGImportHeader = (props: IEKGImportHeaderProps) => {
  const { currentStep } = props

  const { t } = useTranslation()

  const { selectedEvents, selectedReports } = useSelector(getEKGImportSelector)
  const { orientation } = useDeviceMetrics()

  const getVertexCircleClass = (vertexStep: number): string => {
    const noSelectedEvents = !selectedEvents.length
    const noSelectedReports = !selectedReports.length

    if (currentStep > vertexStep) {
      if (vertexStep === 2 && noSelectedEvents) return 'skipped-circle'
      if (vertexStep === 3 && noSelectedReports) return 'skipped-circle'
      return 'completed-circle'
    }

    if (currentStep === vertexStep) {
      return 'active-circle'
    }

    return 'inactive-circle'
  }

  const getVertexLabelClass = (vertexStep: number): string => {
    if (currentStep > vertexStep) {
      return 'completed-label'
    }

    if (currentStep === vertexStep) {
      return 'active-label'
    }

    return 'inactive-label'
  }

  const renderVertex = (vertexStep: number, title: string) => {
    const circleClass = getVertexCircleClass(vertexStep)
    const labelClass = getVertexLabelClass(vertexStep)

    return (
      <div className='wizard-vertex'>
        <div className={`wizard-vertex-label ${labelClass}`}>{title}</div>
        <div className={`wizard-vertex-circle ${circleClass}`}>
          {circleClass === 'skipped-circle' && <img src={skippedIcon} alt='' />}
          {circleClass === 'completed-circle' && (
            <IonIcon className='completed-circle-checkmark' color='white' icon={checkmark} />
          )}
          {circleClass === 'inactive-circle' && <div className='wizard-vertex-circle-inner' />}
        </div>
      </div>
    )
  }

  const getSegmentClass = (segmentIndex: number): string => {
    return currentStep > segmentIndex ? 'wizard-segment-filled' : ''
  }

  const renderSegment = (segmentIndex: number) => {
    const segmentClass = getSegmentClass(segmentIndex)
    return (
      <div className='wizard-segment-wrapper'>
        <div className={`wizard-segment ${segmentClass}`} />
      </div>
    )
  }

  const renderWizard = () => {
    return (
      <div className='wizard'>
        {renderVertex(1, 'Monitor')}
        {renderSegment(1)}
        {renderVertex(2, 'Events')}
        {renderSegment(2)}
        {renderVertex(3, 'Reports')}
        {renderSegment(3)}
        {renderVertex(4, 'Import')}
      </div>
    )
  }

  const renderStepTitle = (): string => {
    const isPortrait = orientation === 'portrait'
    const poweredBy = isPortrait ? t('ekg.poweredByShort') : t('ekg.poweredByLong')

    if (currentStep === 1) return `ECGs | ${poweredBy}`
    if (currentStep === 2) return 'ECGs | Events'
    if (currentStep === 3) return 'ECGs | Reports'

    return 'ECGs | Summary'
  }

  return (
    <>
      <div className='ekg-header-step-title'>{renderStepTitle()}</div>
      {renderWizard()}
    </>
  )
}

export default EKGImportHeader
